import { Modal } from '../Common/Modal';
import { hasPublisherHaveBackgroundImage } from '@/utils/publisher';
import type { CheckoutTheme } from '@/types/boot';
import { useGetBoot } from '@/hooks/useGetBoot';
import { peekBootConfigSync } from '@/utils/boot';
import { FeatureFlag } from '@/constants/featureFlags';
import { useIsMobile } from '@/hooks/useIsMobile';
import TopWrapperSkeleton from './TopWrapperSkeleton';
import PaymentSkeleton from './PaymentSkeleton';
import OnePaymentSkeleton from './OnePaymentSkeleton';
import PreferredPaymentSkeleton from './PreferredPaymentSkeleton';
import EmbeddedViewSkeleton from './EmbeddedViewSkeleton';
import EmbeddedPreferredSkeleton from './EmbeddedPreferredSkeleton';
import { TemplateType } from '@/types/boot';

/**
 * Per-template boot skeleton dispatcher. Reads the template enum from boot
 * (defaults to DEFAULT before boot resolves) and renders the matching
 * skeleton so the layout the user sees during loading is the same shape as
 * the layout that paints when content arrives.
 *
 * - DEFAULT  → existing two-pane (TopWrapper + Payment) skeleton.
 * - SINGLE   → `OnePaymentSkeleton`     — compact card mirror.
 * - PREFERRED→ `PreferredPaymentSkeleton` on MOBILE only. On desktop the
 *               `PreferredPaymentView` itself falls back to `DefaultView`
 *               (full hero pane + payment column), so painting the mobile
 *               skeleton on desktop would flash a totally different shape
 *               for one frame. We mirror the view's desktop fallback by
 *               rendering the DEFAULT two-pane skeleton instead.
 *
 * If boot eligibility ends up falling back from PREFERRED/SINGLE to DEFAULT
 * (e.g. missing payment method), the skeleton was a one-frame mismatch —
 * acceptable at boot, the swap absorbs it.
 */
const CheckoutSkeletonLoader = () => {
  const bootData = useGetBoot();
  const isMobile = useIsMobile();
  // Drawer FF ON only: before the verified boot lands, use the synchronous
  // unverified peek to route PREFERRED-mobile to the compact bottom-sheet
  // skeleton on the FIRST render (no DEFAULT full-screen flash before the
  // sheet). With the flag OFF/absent this is byte-identical to before — the
  // verified template, or DEFAULT until `getBoot` resolves.
  const verifiedTemplate = bootData.boot?.checkoutConfiguration?.template;
  const drawerHint = verifiedTemplate ? null : peekBootConfigSync();
  const templateType =
    verifiedTemplate ??
    (drawerHint?.featureFlags?.[FeatureFlag.CHECKOUT_DRAWER_FOR_LAST_METHOD]
      ? drawerHint.checkoutConfiguration?.template
      : undefined) ??
    TemplateType.DEFAULT;

  if (templateType === TemplateType.SINGLE_PAYMENT_METHOD) {
    return <OnePaymentSkeleton />;
  }
  if (templateType === TemplateType.PREFERRED_PAYMENT_METHOD && isMobile) {
    return <PreferredPaymentSkeleton />;
  }
  if (templateType === TemplateType.EMBEDDED_PREFERRED_PAYMENT_METHOD) {
    return <EmbeddedPreferredSkeleton />;
  }
  if (templateType === TemplateType.EMBEDDED_VIEW) {
    return <EmbeddedViewSkeleton />;
  }

  const checkoutTheme = bootData.boot?.checkoutTheme as CheckoutTheme | undefined;
  const hasBackgroundImage = hasPublisherHaveBackgroundImage(checkoutTheme, templateType);
  const backgroundImage = hasBackgroundImage
    ? isMobile
      ? checkoutTheme?.mobileBgImage || checkoutTheme?.backgroundImage
      : checkoutTheme?.backgroundImage
    : undefined;

  return (
    <Modal>
      <div className="flex flex-col sm:justify-center md:flex-row md:justify-start md:h-full overflow-y-auto md:overflow-y-hidden bg-white landscape:flex-row landscape:h-[100dvh] landscape:overflow-y-hidden landscape:overflow-x-hidden">
        <div
          className="flex flex-col items-center py-4 justify-center md:w-2/5 md:justify-start bg-cover bg-center bg-top md:h-full landscape:w-2/5 landscape:h-[100dvh] landscape:justify-start"
          style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
        >
          <TopWrapperSkeleton />
        </div>
        <div className="relative flex flex-col items-center py-4 md:w-3/5 md:h-full md:overflow-y-auto landscape:w-3/5 landscape:h-[100dvh] landscape:overflow-y-auto">
          <div className="flex w-full justify-center items-center h-full">
            <PaymentSkeleton />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CheckoutSkeletonLoader;
